import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Hook for passwordless dev-user sign-in.
 * Calls the dev-sign-in edge function which generates a session
 * server-side without requiring a password.
 */
export function useDevSignIn() {
  const [isLoading, setIsLoading] = useState(false);

  const devSignIn = async (email: string): Promise<{ error: Error | null }> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("dev-sign-in", {
        body: { email },
      });

      if (error) {
        logger.error("Dev sign-in invoke error:", error);
        return { error: new Error(error.message || "Dev sign-in failed") };
      }

      // Dual-Error-Check: application-level error from edge function
      if (!data || data.error) {
        return { error: new Error(data?.error || "Dev sign-in failed") };
      }

      if (!data.access_token || !data.refresh_token) {
        return { error: new Error("Invalid response from dev sign-in") };
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });

      if (sessionError) {
        logger.error("Set session error:", sessionError);
        return { error: sessionError };
      }

      return { error: null };
    } catch (err) {
      logger.error("Dev sign-in exception:", err);
      return { error: err instanceof Error ? err : new Error("Dev sign-in failed") };
    } finally {
      setIsLoading(false);
    }
  };

  return { devSignIn, isLoading };
}
